import {useCallback, useRef, useState} from 'react';
import type {
  AssistantChartResponse,
  AssistantMemoryResponse,
  AssistantStopReasonEnum,
  AssistantTableResponse,
} from 'src/api/ProactApiClient';
import {useAppDispatch} from 'src/redux/hooks';
import {
  invalidateAssistantMemory,
  invalidateAssistantUsage,
  useGetConversationMessagesQuery,
} from 'src/redux/queries/assistant';
import type {AssistantMemoryAction} from 'src/shared/assistant/streamAssistantMessage';
import {streamAssistantMessage} from 'src/shared/assistant/streamAssistantMessage';

export type StreamItem =
  | {kind: 'text'; text: string}
  | {
      kind: 'tool';
      callId: string;
      tool: string;
      running: boolean;
      isError?: boolean;
    }
  | {kind: 'chart'; chart: AssistantChartResponse}
  | {kind: 'table'; table: AssistantTableResponse}
  | {kind: 'memory'; note: AssistantMemoryResponse; action: AssistantMemoryAction};

export interface StreamDone {
  messageId: number;
  stopReason: AssistantStopReasonEnum | undefined;
  usedMemoryIds: number[];
}

export interface SendMessageOptions {
  thinkHarder?: boolean;
}

// Stable identity for the suppressed-turn case so consumers don't re-fire effects.
const EMPTY_STREAM_ITEMS: StreamItem[] = [];

export interface UseAssistantChatResult {
  messages: ReturnType<typeof useGetConversationMessagesQuery>['data'];
  isMessagesLoading: boolean;
  isMessagesError: boolean;
  pendingUserContent: string | null;
  streamItems: StreamItem[];
  activeTool: string | null;
  isStreaming: boolean;
  streamError: string | null;
  /** The finished reply's `done` event, until the refetched list carries the turn. */
  streamDone: StreamDone | null;
  sendMessage: (content: string, options?: SendMessageOptions) => Promise<void>;
  stop: () => void;
}

export const useAssistantChat = (
  conversationId: number
): UseAssistantChatResult => {
  const {
    data: messages,
    isLoading: isMessagesLoading,
    isError: isMessagesError,
    refetch,
  } = useGetConversationMessagesQuery(conversationId);

  const dispatch = useAppDispatch();

  const [pendingUserContent, setPendingUserContent] = useState<string | null>(
    null
  );
  const [streamItems, setStreamItems] = useState<StreamItem[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [streamDone, setStreamDone] = useState<StreamDone | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Server message count snapshotted at send start; once the refetched list
  // grows past it the new turn is server-side, so local bubbles are suppressed.
  const sentAtCountRef = useRef<number | null>(null);

  const sendMessage = useCallback(
    async (content: string, options?: SendMessageOptions) => {
      const trimmed = content.trim();
      if (!trimmed || isStreaming) return;

      const controller = new AbortController();
      abortRef.current = controller;

      sentAtCountRef.current = messages?.length ?? 0;
      setPendingUserContent(trimmed);
      setStreamItems([]);
      setStreamError(null);
      setStreamDone(null);
      setIsStreaming(true);

      try {
        await streamAssistantMessage({
          conversationId,
          content: trimmed,
          thinkHarder: options?.thinkHarder ?? false,
          signal: controller.signal,
          onEvent: event => {
            switch (event.type) {
              case 'text':
                setStreamItems(prev => {
                  const last = prev[prev.length - 1];
                  if (last?.kind === 'text') {
                    const next = prev.slice(0, -1);
                    next.push({kind: 'text', text: last.text + event.text});
                    return next;
                  }
                  return [...prev, {kind: 'text', text: event.text}];
                });
                break;
              case 'tool_started':
                setStreamItems(prev => [
                  ...prev,
                  {
                    kind: 'tool',
                    callId: event.callId,
                    tool: event.tool,
                    running: true,
                  },
                ]);
                break;
              case 'tool_finished':
                setStreamItems(prev => {
                  const index = prev.findIndex(
                    item => item.kind === 'tool' && item.callId === event.callId
                  );
                  if (index === -1) return prev;
                  const next = prev.slice();
                  const target = next[index] as Extract<
                    StreamItem,
                    {kind: 'tool'}
                  >;
                  next[index] = {
                    ...target,
                    running: false,
                    isError: event.isError,
                  };
                  return next;
                });
                break;
              case 'chart':
                setStreamItems(prev => [
                  ...prev,
                  {kind: 'chart', chart: event.chart},
                ]);
                break;
              case 'table':
                setStreamItems(prev => [
                  ...prev,
                  {kind: 'table', table: event.table},
                ]);
                break;
              case 'memory':
                setStreamItems(prev => [
                  ...prev,
                  {kind: 'memory', note: event.note, action: event.action},
                ]);
                dispatch(invalidateAssistantMemory());
                break;
              case 'error':
                setStreamError(event.message);
                break;
              case 'done':
                setStreamDone({
                  messageId: event.messageId,
                  stopReason: event.stopReason,
                  usedMemoryIds: event.usedMemoryIds,
                });
                break;
            }
          },
        });
      } catch {
        if (!controller.signal.aborted) {
          setStreamError('The Scout connection was interrupted.');
        }
      }

      // Drop the spinner now; clear local bubbles only after the refetch so the
      // returned-value derivation can bridge the gap without a duplicate frame.
      setIsStreaming(false);
      abortRef.current = null;
      await refetch();
      dispatch(invalidateAssistantUsage());
      setPendingUserContent(null);
      setStreamItems([]);
      setStreamDone(null);
    },
    [conversationId, dispatch, isStreaming, messages, refetch]
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // Suppress local bubbles in the same render the refetched list gains the new
  // turn, so there is no vanish/pop gap and no server+local duplicate frame.
  const serverHasNewTurn =
    sentAtCountRef.current !== null &&
    (messages?.length ?? 0) > sentAtCountRef.current;

  const runningTool = isStreaming
    ? [...streamItems].reverse().find(item => item.kind === 'tool' && item.running)
    : undefined;
  const activeTool = runningTool?.kind === 'tool' ? runningTool.tool : null;

  return {
    messages,
    isMessagesLoading,
    isMessagesError,
    pendingUserContent: serverHasNewTurn ? null : pendingUserContent,
    streamItems: serverHasNewTurn ? EMPTY_STREAM_ITEMS : streamItems,
    activeTool,
    isStreaming,
    streamError,
    streamDone: serverHasNewTurn ? null : streamDone,
    sendMessage,
    stop,
  };
};
